import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const SignupSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentData, setPaymentData] = useState<any>(null);

  const sessionId = searchParams.get('session_id');

  const createUserAccount = async (paymentData: any) => {
    try {
      // Get stored signup data from localStorage
      const storedData = localStorage.getItem('eightythreelabs_signup_data');
      if (!storedData) {
        console.warn('No signup data found in localStorage');
        return;
      }

      const signupData = JSON.parse(storedData);
      console.log('Creating account with signup data:', { email: signupData.email, tier: signupData.tier });

      // Create account using the create-account-after-checkout worker
      const createAccountUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:8790'
        : 'https://eightythreelabs-create-account-after-checkout.yahya-darman-work.workers.dev';

      const response = await fetch(`${createAccountUrl}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId, // Use session_id instead of payment_intent_id
          email: signupData.email,
          password: signupData.password,
          tier: signupData.tier,
          agreed_to_terms: signupData.agreedToTerms,
          stripe_customer_id: paymentData.customer_id,
          subscription_id: paymentData.subscription_id
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Account created successfully:', result);
        // Clear the stored signup data
        localStorage.removeItem('eightythreelabs_signup_data');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to create account:', errorData);
      }
    } catch (error) {
      console.error('Error creating user account:', error);
    }
  };

  const downloadReceipt = (paymentData: any) => {
    try {
      // Create receipt content as HTML
      const receiptDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      const receiptTime = new Date().toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });
      
      const invoiceNumber = `INV-${sessionId?.substring(8, 18)?.toUpperCase() || 'UNKNOWN'}`;
      const amount = ((paymentData.amount_total || 0) / 100);
      const currency = (paymentData.currency || 'USD').toUpperCase();
      
      const receiptContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>EightyThreeLabs Official Receipt</title>
            <meta charset="utf-8">
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
              
              body { 
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
                max-width: 700px; 
                margin: 0 auto; 
                padding: 40px 30px; 
                background: #ffffff;
                color: #1f2937;
                line-height: 1.6;
              }
              
              .invoice-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                margin-bottom: 40px;
                padding-bottom: 25px;
                border-bottom: 3px solid #3B82F6;
              }
              
              .company-info {
                flex: 1;
              }
              
              .company-logo {
                font-size: 28px;
                font-weight: 700;
                color: #3B82F6;
                margin-bottom: 8px;
              }
              
              .company-tagline {
                font-size: 14px;
                color: #6b7280;
                margin-bottom: 15px;
              }
              
              .company-details {
                font-size: 13px;
                color: #4b5563;
                line-height: 1.4;
              }
              
              .invoice-info {
                text-align: right;
                flex: 1;
              }
              
              .invoice-title {
                font-size: 32px;
                font-weight: 700;
                color: #1f2937;
                margin-bottom: 10px;
              }
              
              .invoice-number {
                font-size: 16px;
                font-weight: 600;
                color: #3B82F6;
                margin-bottom: 5px;
              }
              
              .invoice-date {
                font-size: 14px;
                color: #6b7280;
              }
              
              .customer-section {
                background: #f9fafb;
                padding: 25px;
                border-radius: 12px;
                margin-bottom: 30px;
                border-left: 4px solid #3B82F6;
              }
              
              .customer-title {
                font-size: 16px;
                font-weight: 600;
                color: #1f2937;
                margin-bottom: 12px;
              }
              
              .customer-email {
                font-size: 15px;
                font-weight: 500;
                color: #3B82F6;
                font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, monospace;
              }
              
              .services-table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 30px;
                background: white;
                border-radius: 12px;
                overflow: hidden;
                box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
              }
              
              .services-table th {
                background: #f3f4f6;
                padding: 18px;
                text-align: left;
                font-weight: 600;
                font-size: 14px;
                color: #374151;
                border-bottom: 1px solid #e5e7eb;
              }
              
              .services-table td {
                padding: 18px;
                border-bottom: 1px solid #f3f4f6;
                font-size: 14px;
              }
              
              .service-name {
                font-weight: 500;
                color: #1f2937;
              }
              
              .service-description {
                font-size: 13px;
                color: #6b7280;
                margin-top: 2px;
              }
              
              .price {
                font-weight: 600;
                text-align: right;
                color: #1f2937;
              }
              
              .total-section {
                background: #f9fafb;
                padding: 25px;
                border-radius: 12px;
                margin-bottom: 30px;
              }
              
              .total-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 8px 0;
                font-size: 15px;
              }
              
              .total-final {
                border-top: 2px solid #e5e7eb;
                padding-top: 15px;
                margin-top: 15px;
                font-size: 20px;
                font-weight: 700;
              }
              
              .total-final .amount {
                color: #059669;
              }
              
              .payment-info {
                background: #ecfdf5;
                border: 1px solid #d1fae5;
                padding: 20px;
                border-radius: 12px;
                margin-bottom: 30px;
              }
              
              .payment-status {
                display: flex;
                align-items: center;
                font-weight: 600;
                color: #059669;
                margin-bottom: 15px;
                font-size: 16px;
              }
              
              .check-icon {
                width: 20px;
                height: 20px;
                margin-right: 8px;
                color: #059669;
              }
              
              .payment-details {
                font-size: 14px;
                color: #065f46;
                line-height: 1.5;
              }
              
              .transaction-ids {
                background: #fafafa;
                padding: 15px;
                border-radius: 8px;
                margin-top: 15px;
                font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, monospace;
                font-size: 12px;
                color: #4b5563;
              }
              
              .footer {
                text-align: center;
                padding-top: 30px;
                border-top: 1px solid #e5e7eb;
                margin-top: 40px;
              }
              
              .footer-title {
                font-size: 16px;
                font-weight: 600;
                color: #1f2937;
                margin-bottom: 10px;
              }
              
              .footer-text {
                font-size: 13px;
                color: #6b7280;
                margin-bottom: 8px;
              }
              
              .contact-info {
                font-size: 13px;
                color: #3B82F6;
                font-weight: 500;
              }
              
              .tax-notice {
                background: #fef3c7;
                border: 1px solid #f59e0b;
                padding: 15px;
                border-radius: 8px;
                margin-top: 20px;
                font-size: 12px;
                color: #92400e;
              }
              
              @media print {
                body { padding: 20px; }
                .invoice-header { page-break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            <div class="invoice-header">
              <div class="company-info">
                <div class="company-logo">🧠 EightyThreeLabs</div>
                <div class="company-tagline">Advanced Math Learning Platform</div>
                <div class="company-details">
                  Premium Educational Services<br>
                  contact@eightythreelabs.com<br>
                  www.eightythreelabs.com
                </div>
              </div>
              <div class="invoice-info">
                <div class="invoice-title">RECEIPT</div>
                <div class="invoice-number">${invoiceNumber}</div>
                <div class="invoice-date">${receiptDate}</div>
                <div class="invoice-date">${receiptTime}</div>
              </div>
            </div>
            
            <div class="customer-section">
              <div class="customer-title">Bill To:</div>
              <div class="customer-email">${paymentData.customer_email || 'Customer'}</div>
            </div>
            
            <table class="services-table">
              <thead>
                <tr>
                  <th style="width: 60%;">Service Description</th>
                  <th style="width: 15%;">Period</th>
                  <th style="width: 10%;">Qty</th>
                  <th style="width: 15%;">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div class="service-name">${(paymentData.tier || 'Elite').charAt(0).toUpperCase() + (paymentData.tier || 'elite').slice(1)} Subscription</div>
                    <div class="service-description">
                      Premium access to all math learning content, interactive exercises, 
                      personalized learning paths, progress tracking, and analytics
                    </div>
                  </td>
                  <td>Annual</td>
                  <td>1</td>
                  <td class="price">$${amount.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
            
            <div class="total-section">
              <div class="total-row">
                <span>Subtotal:</span>
                <span>$${amount.toFixed(2)} ${currency}</span>
              </div>
              <div class="total-row">
                <span>Taxes:</span>
                <span>$0.00 ${currency}</span>
              </div>
              <div class="total-row">
                <span>Discounts:</span>
                <span>$0.00 ${currency}</span>
              </div>
              <div class="total-row total-final">
                <span>Total Paid:</span>
                <span class="amount">$${amount.toFixed(2)} ${currency}</span>
              </div>
            </div>
            
            <div class="payment-info">
              <div class="payment-status">
                <svg class="check-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                </svg>
                Payment Successful
              </div>
              <div class="payment-details">
                Payment Method: Credit/Debit Card via Stripe<br>
                Payment Date: ${receiptDate} at ${receiptTime}<br>
                Status: <strong>PAID IN FULL</strong>
              </div>
              <div class="transaction-ids">
                <strong>Transaction Reference:</strong><br>
                Session ID: ${sessionId}<br>
                Subscription ID: ${paymentData.subscription_id || 'Processing...'}
              </div>
            </div>
            
            <div class="footer">
              <div class="footer-title">Thank you for choosing EightyThreeLabs!</div>
              <div class="footer-text">Your premium subscription is now active and ready to use.</div>
              <div class="footer-text">This receipt serves as proof of payment for your records.</div>
              <div class="contact-info">
                Questions? Contact us at contact@eightythreelabs.com
              </div>
              <div class="tax-notice">
                <strong>Important:</strong> This is an official receipt for tax and accounting purposes. 
                Please retain this document for your financial records.
              </div>
            </div>
          </body>
        </html>
      `;

      // Create and download the receipt
      const blob = new Blob([receiptContent], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `EightyThreeLabs-Receipt-${invoiceNumber}-${new Date().getFullYear()}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Show success message
      console.log('Receipt downloaded successfully');
    } catch (error) {
      console.error('Failed to download receipt:', error);
      alert('Failed to download receipt. Please contact support.');
    }
  };

  useEffect(() => {
    // If user is already logged in, redirect to dashboard
    if (user) {
      navigate('/');
      return;
    }

    // Verify the checkout session was successful
    if (!sessionId) {
      setError('No session ID found. Please try signing up again.');
      setLoading(false);
      return;
    }

    // Verify the session with our backend
    const verifySession = async () => {
      try {
        const verifyUrl = process.env.NODE_ENV === 'development' 
          ? 'http://localhost:8789' 
          : 'https://eightythreelabs-verify-checkout-session.yahya-darman-work.workers.dev';

        const response = await fetch(`${verifyUrl}/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            session_id: sessionId
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (!data.valid) {
          throw new Error(data.error || 'Session verification failed');
        }

        // Session is verified and payment completed
        console.log('Payment verified:', data);
        setPaymentData(data);

        // Now create the user account since payment is confirmed
        await createUserAccount(data);

        setLoading(false);
        
      } catch (error) {
        console.error('Session verification failed:', error);
        if (error instanceof TypeError && error.message.includes('fetch')) {
          setError('Unable to connect to verification service. Please check your internet connection.');
        } else if (error instanceof Error) {
          setError(error.message);
        } else {
          setError('Failed to verify payment. Please contact support.');
        }
        setLoading(false);
      }
    };

    verifySession();
  }, [sessionId, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-2">
            Verifying Payment...
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            Please wait while we confirm your subscription
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 text-center">
          <div className="text-6xl mb-6">❌</div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            Payment Verification Failed
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {error}
          </p>
          <div className="space-y-4">
            <Link
              to="/signup"
              className="block w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Try Again
            </Link>
            <Link
              to="/"
              className="block w-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 text-center">
        <div className="text-6xl mb-6">🎉</div>
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-4">
          Payment Successful!
        </h1>
        
        <div className="bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-green-800 dark:text-green-200 mb-2">
            🎉 Payment Confirmed!
          </h2>
          <p className="text-green-700 dark:text-green-300 text-sm mb-2">
            Your {paymentData?.tier || 'elite'} subscription has been successfully activated.
          </p>
          <p className="text-green-600 dark:text-green-400 text-xs">
            Amount paid: ${((paymentData?.amount_total || 0) / 100).toFixed(2)} {(paymentData?.currency || 'usd').toUpperCase()}
          </p>
        </div>

        <div className="space-y-4 mb-8">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Subscription Plan:</span>
            <span className="font-semibold text-gray-800 dark:text-gray-200 capitalize">{paymentData?.tier || 'Premium'} Access</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Amount Paid:</span>
            <span className="font-semibold text-gray-800 dark:text-gray-200">${((paymentData?.amount_total || 0) / 100).toFixed(2)}/year</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Subscription ID:</span>
            <span className="font-mono text-xs text-gray-600 dark:text-gray-400">{paymentData?.subscription_id || 'Processing...'}</span>
          </div>
          <div className="flex items-center justify-between text-sm border-t pt-2 mt-2">
            <span className="text-gray-600 dark:text-gray-400">Status:</span>
            <span className="font-bold text-green-600 dark:text-green-400">✅ Active</span>
          </div>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
          <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
            📧 Your Login Email
          </h3>
          <p className="text-blue-700 dark:text-blue-300 text-sm mb-2">
            Use this email address to sign in to your account:
          </p>
          <p className="text-blue-800 dark:text-blue-200 font-mono text-sm bg-blue-100 dark:bg-blue-900/40 px-3 py-1 rounded">
            {paymentData?.customer_email || 'your email address'}
          </p>
        </div>
        
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">
            🚀 Ready to Go!
          </h3>
          <div className="text-amber-700 dark:text-amber-300 text-sm space-y-1">
            <p><strong>Your account is ready!</strong> You can now:</p>
            <p>1. Click "Sign In to Your Account" below</p>
            <p>2. Access your premium features on the dashboard</p>
            <p>3. Download your receipt for your records</p>
          </div>
        </div>

        <div className="space-y-3">
          <Link
            to="/login"
            className="block w-full bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors"
          >
            Sign In to Your Account
          </Link>
          
          {paymentData && (
            <button
              onClick={() => downloadReceipt(paymentData)}
              className="block w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Receipt
            </button>
          )}
          
          <Link
            to="/"
            className="block w-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Back to Home
          </Link>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Questions? Contact us at{' '}
            <a href="mailto:contact@eightythreelabs.com" className="text-blue-600 dark:text-blue-400 hover:underline">
              contact@eightythreelabs.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupSuccess;